import React, { useContext, useEffect, useState } from 'react'
import AuthContext from '../../../context/AuthContext';
import userService from '../../../services/userService';
import useLoader from '../../../context/useLoader';
import { useToast } from '../../../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Calendar, 
  Clock, 
  User, 
  Users, 
  AlertCircle, 
  Activity, 
  Layers, 
  CheckSquare,
  TrendingUp,
  Shield,
  Mail,
  CheckCircle,
  XCircle,
  History,
  Users2,
  Check,
  Cross,
  X
} from 'lucide-react';

export default function CompanyInfo() {
  const navigate = useNavigate();
  const { user, hasRole, authTokens } = useContext(AuthContext);  
  const [organizationInfos, setOrganizationInfos] = useState(null);
  const { showSuccess, showError } = useToast();
  const { isLoading, showLoader, hideLoader } = useLoader();

  useEffect(() => {
    fetchOrganizationInfos()
  }, [])

  const fetchOrganizationInfos = async() => {
    showLoader("Chargement des informations sur l'organisation")

    try {
      const result = await userService.getOrganizationInfos(authTokens.access)
      if (result.success) {
        console.log(result)
        setOrganizationInfos(result.data)
      } else {
        showError(`Erreur lors du chargement: ${result.error}`);
        setTimeout(() => navigate(-1), 2000);
      }
    } catch(error) {
      console.error('Error fetching organization infos:', error);
      showError('Impossible de charger les détails de l\'organisation');
      setTimeout(() => navigate(-1), 2000);
    } finally {
      hideLoader();
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getSubscriptionStatusBadge = (status, isActive) => {
    const baseClasses = "px-3 py-1 rounded-full text-sm font-medium";
    if (isActive && status === 'active') {
      return `${baseClasses} bg-green-100 text-green-800`;
    } else if (status === 'expired') {
      return `${baseClasses} bg-red-100 text-red-800`;
    } else if (status === 'cancelled') {
      return `${baseClasses} bg-gray-100 text-gray-800`;
    }
    return `${baseClasses} bg-yellow-100 text-yellow-800`;
  };

  const getActionBadge = (action) => {
    const badges = {
      'subscribe': 'bg-green-100 text-green-800',
      'unsubscribe': 'bg-red-100 text-red-800',
      'renew': 'bg-blue-100 text-blue-800',
      'extend': 'bg-teal-100 text-teal-800',
      'expire': 'bg-gray-100 text-gray-800',
      'reactivate': 'bg-purple-100 text-purple-800',
      'cancel': 'bg-orange-100 text-orange-800',
      'suspend': 'bg-yellow-100 text-yellow-800',
      'update': 'bg-indigo-100 text-indigo-800',
    };
    return badges[action] || 'bg-gray-100 text-gray-800';
  };

  if (!organizationInfos) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  const { organization, subscription, statistics, subscription_history } = organizationInfos;

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Building2 className="h-8 w-8 text-teal-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">{organization.organisation_name}</h1>
            <p className="text-sm text-gray-500">Informations de l'organisation</p>
          </div>
        </div>
        <span className={organization.is_active 
          ? "px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800" 
          : "px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800"
        }>
          {organization.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Organization Details */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Détails de l'organisation</h2>
        
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-500">Description</label>
            <p className="mt-1 text-gray-900 text-sm">{organization.organisation_description || 'Aucune description'}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-gray-200">
            <div className="flex items-start space-x-2">
              <Calendar className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <label className="text-sm font-medium text-gray-500">Date de création</label>
                <p className="text-sm text-gray-900">{formatDate(organization.created_at)}</p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <Clock className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <label className="text-sm font-medium text-gray-500">Dernière mise à jour</label>
                <p className="text-sm text-gray-900">{formatDate(organization.updated_at)}</p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <User className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <label className="text-sm font-medium text-gray-500">Créé par</label>
                <p className="text-sm text-gray-900">{organization.created_by}</p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <User className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <label className="text-sm font-medium text-gray-500">Dernière modification par</label>
                <p className="text-sm text-gray-900">{organization.last_updated_by}</p>
              </div>
            </div>
          </div>

          {!organization.is_active && organization.deactivation_reason && (
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-start space-x-2 text-red-600">
                <AlertCircle className="h-4 w-4 mt-0.5" />
                <div>
                  <label className="text-sm font-medium">Raison de la désactivation</label>
                  <p className="text-sm">{organization.deactivation_reason}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Status */}
      {subscription && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Abonnement</h2>
            <span className={getSubscriptionStatusBadge(subscription.status, subscription.is_active)}>
              {subscription.status}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start space-x-2">
              <Calendar className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <label className="text-sm font-medium text-gray-500">Date de début</label>
                <p className="text-sm text-gray-900">{formatDate(subscription.start_date)}</p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <Calendar className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <label className="text-sm font-medium text-gray-500">Date de fin</label>
                <p className="text-sm text-gray-900">{formatDate(subscription.end_date)}</p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <Clock className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <label className="text-sm font-medium text-gray-500">Jours restants</label>
                <p className="text-sm text-gray-900 font-semibold">
                  {subscription.days_remaining} jour{subscription.days_remaining > 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>


          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
            <div className="flex items-start space-x-2">
              <Users2 className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <label className="text-sm font-medium text-gray-500">Nombre maximum d'utilisateurs</label>
                <p className="text-sm text-gray-900">{organization.max_number_users}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-2">
              <Users2 className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <label className="text-sm font-medium text-gray-500">Ancien nombre maximum d'utilisateurs</label>
                <p className="text-sm text-gray-900">{organization.old_user_max_number}</p>
              </div>
            </div>

 

            <div className="flex items-start space-x-2">
            {organization.canAddUsers? <Check className="h-4 w-4 text-green-400 mt-0.5" />: <X className="h-4 w-4 text-red-400 mt-0.5" /> }
              
              <div>
                <label className="text-sm font-medium text-gray-500">Peut-on encore ajouter des utilisateurs ?</label>
                <p className={organization.canAddUsers? "text-sm text-green-900 font-semibold": "text-sm text-red-900 font-semibold"}>
                  {organization.canAddUsers? "Oui": "Non"}
                </p>
              </div>
            </div>
          </div>


          {subscription.reason && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <label className="text-sm font-medium text-gray-500">Raison</label>
              <p className="text-sm text-gray-900 mt-1">{subscription.reason}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-start space-x-2">
              <Mail className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <label className="text-sm font-medium text-gray-500">Créé par</label>
                <p className="text-sm text-gray-900">{subscription.created_by_email}</p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <Mail className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <label className="text-sm font-medium text-gray-500">Modifié par</label>
                <p className="text-sm text-gray-900">{subscription.last_updated_by_email}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Statistiques</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
            <div className="flex items-center space-x-2 mb-1">
              <Users className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">Utilisateurs</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">{statistics.users_count}</p>
          </div>

          <div className="bg-red-50 rounded-lg p-3 border border-red-100">
            <div className="flex items-center space-x-2 mb-1">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <span className="text-sm font-medium text-red-900">Risques</span>
            </div>
            <p className="text-2xl font-bold text-red-700">{statistics.risks_count}</p>
          </div>

          <div className="bg-green-50 rounded-lg p-3 border border-green-100">
            <div className="flex items-center space-x-2 mb-1">
              <Activity className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-green-900">Activités</span>
            </div>
            <p className="text-2xl font-bold text-green-700">{statistics.activities_count}</p>
          </div>

          <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
            <div className="flex items-center space-x-2 mb-1">
              <Layers className="h-5 w-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-900">Processus</span>
            </div>
            <p className="text-2xl font-bold text-purple-700">{statistics.processes_count}</p>
          </div>

          <div className="bg-teal-50 rounded-lg p-3 border border-teal-100">
            <div className="flex items-center space-x-2 mb-1">
              <CheckSquare className="h-5 w-5 text-teal-600" />
              <span className="text-sm font-medium text-teal-900">Tâches</span>
            </div>
            <p className="text-2xl font-bold text-teal-700">{statistics.process_tasks_count}</p>
          </div>
        </div>
      </div>

      {/* Subscription History */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <History className="h-5 w-5 mr-2 text-gray-600" />
              Historique d'abonnement
            </h2>
            <span className="px-2 py-1 bg-teal-100 text-teal-800 text-xs font-medium rounded-full">
              {subscription_history.length} entrée{subscription_history.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {subscription_history && subscription_history.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {subscription_history.map((history, index) => (
              <div key={index} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getActionBadge(history.action)}`}>
                        {history.action}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDate(history.action_date)}
                      </span>
                    </div>
                    
                    {history.reason && (
                      <p className="text-sm text-gray-700 mb-2">{history.reason}</p>
                    )}

                    {history.notes && (
                      <p className="text-sm text-gray-600 italic">{history.notes}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100 text-xs">
                  {history.subscription_start_date && (
                    <div className="flex items-center space-x-1 text-gray-600">
                      <Calendar className="h-3 w-3" />
                      <span>Début: {formatDate(history.subscription_start_date)}</span>
                    </div>
                  )}
                  
                  {history.subscription_end_date && (
                    <div className="flex items-center space-x-1 text-gray-600">
                      <Calendar className="h-3 w-3" />
                      <span>Fin: {formatDate(history.subscription_end_date)}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-1 text-gray-600">
                    <Mail className="h-3 w-3" />
                    <span>{history.performed_by_email}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <History className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">Aucun historique d'abonnement</p>
          </div>
        )}
      </div>
    </div>
  );
}